import React, { forwardRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { FormFieldValidation } from "store/formTemplate/formTemplateTypes";
import { getNestedError } from "utils/splitDot";
import { mapValidations } from "utils/validationMap";
import { sanitizeForm } from "utils/htmlSanitizer";
import Icon from "../Icon/Icon";
import { IconName } from "../Icon/icons";

type RadioOption = {
  id: string;
  label: string;
  value: string;
  disabled?: boolean;
};

type RadioProps = {
  id?: string;
  name: string; // required for grouping
  label?: string;
  checked?: boolean;
  disabled?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  options?: RadioOption[];
  value?: string;
  register?: (name: string) => any;
  clearOnFill?: string[]; // fields to clear when this field is filled
  defaultValue?: string;
  validations?: FormFieldValidation; // validation rules for react-hook-form
  required?: boolean; // explicitly mark field as required
  atLeastOneOf?: string; // field name that has atLeastOneOf relationship with this field
};

/**
 * RadioInput component for creating accessible radio button inputs with React Hook Form integration.
 * 
 * Supports both single radio buttons and radio button groups with options.
 * Provides validation, error handling, and dynamic field clearing functionality.
 * 
 * @component
 * @param {RadioProps} props - The props for the RadioInput component
 * @param {string} props.name - Required name for form field registration and grouping
 * @param {RadioOption[]} [props.options] - Array of options for radio group. If provided, renders multiple radio buttons
 * @param {string} [props.id] - Unique identifier for single radio button
 * @param {string} [props.label] - Label text to display next to radio button
 * @param {string} [props.value] - Value for single radio button
 * @param {boolean} [props.checked=false] - Initial checked state for single radio button
 * @param {boolean} [props.disabled=false] - Whether the radio button(s) are disabled
 * @param {string} [props.className=""] - Additional CSS classes
 * @param {string} [props.defaultValue] - Default selected value for form initialization
 * @param {FormFieldValidation} [props.validations] - Validation rules for the field
 * @param {boolean} [props.required=false] - Whether the field is required
 * @param {string[]} [props.clearOnFill] - Field names to clear when this radio button is selected
 * @param {Function} [props.onChange] - Change event handler
 * @param {Function} [props.onClick] - Click event handler
 * @param {Function} [props.register] - React Hook Form register function (deprecated, use Controller instead)
 * 
 * @returns {JSX.Element} The rendered radio input component
 * 
 * @example
 * // Single radio button
 * <RadioInput
 *   name="agreement"
 *   label="I agree to terms"
 *   value="yes"
 *   required={true}
 *   validations={{ type: 'required', message: 'You must agree to continue' }}
 * />
 * 
 * @example
 * // Radio group with options
 * <RadioInput
 *   name="petType"
 *   options={[
 *     { id: "dog", label: "Dog", value: "dog" },
 *     { id: "cat", label: "Cat", value: "cat" }
 *   ]}
 *   required={true}
 *   validations={{ type: 'required', message: 'Please select a pet type' }}
 * />
 */

const RadioInput = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      id,
      name,
      label,
      checked = false,
      disabled = false,
      onChange,
      onClick,
      className = "",
      options,
      value,
      register,
      clearOnFill,
      defaultValue,
      validations,
      required = false,
      atLeastOneOf,
    },
    ref
  ) => {
    const {
      control,
      formState: { errors, touchedFields },
      setValue,
      getValues,
      trigger
    } = useFormContext();

    // Get error message with better handling for required fields
    const errorMessage = name && getNestedError(errors, name)?.message
      ? getNestedError(errors, name).message
      : undefined;

    // Only show errors if field has been touched
    // Don't use isSubmitted because form might be submitted from previous stage
    const isTouched = name && getNestedError(touchedFields, name);
    const shouldShowError = isTouched;

    /**
     * Initialize field value with defaultValue only on first mount
     * Controller will handle the value after that
     */
    const formValue = getValues(name);
    const shouldUseDefault = (formValue === undefined || formValue === null || formValue === "");
    const initialValue = shouldUseDefault && defaultValue ? defaultValue : (formValue || "");

    const handleDynamicClear = (e: React.ChangeEvent<HTMLInputElement>, value: string) => {
      const choiseFieldName = e.target.name;
      const [petType, , indexStr] = choiseFieldName.split(".");
      const index = Number(indexStr);
      
      if (value !== "" && clearOnFill && clearOnFill.length > 0) {
        clearOnFill.forEach((fieldName: string) => {
          const fieldNameEl = petType + "." + fieldName + "." + index;
          setValue(fieldNameEl, "");
          const inputEl = document.querySelector(`input[name="${fieldNameEl}"]`);
          if (inputEl && inputEl instanceof HTMLInputElement) {
            inputEl.value = "";
          }
        });
      }
    };

    if (options && options.length > 0) {
      // Build validation rules
      const validationRules = mapValidations(validations ? [validations] : []);
      
      // Add required validation if explicitly marked as required
      if (required && !validationRules.required) {
        validationRules.required = {
          value: true,
          message: "This field is required"
        };
      }
      
      // Add custom validation to handle empty string as invalid for required fields
      if (required || validationRules.required) {
        const existingValidate = validationRules.validate;
        validationRules.validate = (value: any) => {
          // For radio buttons, empty string, null, or undefined means not selected
          if (required && (!value || value === "")) {
            return validationRules.required?.message || "This field is required";
          }
          
          // Run any existing validation
          if (existingValidate && typeof existingValidate === 'function') {
            return existingValidate(value);
          }
          
          return true;
        };
      }

      return (
        <Controller
          name={name}
          control={control}
          defaultValue={initialValue}
          rules={validationRules}
          render={({ field }) => {


            // Ensure field.value is properly synced with form state
            const currentValue = field.value || defaultValue || "";

            return (
              <>
                {/* Render the main field label if provided */}
                {label && (
                  <div className="fs-16px fw-normal text-black">
                    <span dangerouslySetInnerHTML={{ __html: sanitizeForm(label) }} />
                  </div>
                )}
                {options.map((option) => {
                  const isChecked = currentValue === option.value;
                  
                  return (
                    <label
                      key={name + option.id}
                      htmlFor={name + option.id}
                      className={`cursor-pointer d-inline-flex align-items-md-center gap-5px ${(option.disabled || disabled) ? "opacity-50" : ""}`}
                    >
                      <span
                        role="radio"
                        aria-checked={isChecked ? "true" : "false"}
                        aria-disabled={(option.disabled || disabled) ? "true" : "false"}
                        title={option.label}
                      >
                        <Icon
                          name={isChecked ? "radioFilled" : "radioEmpty"}
                          size={24}
                        />
                      </span>
                      <span
                        className="fs-16px fw-normal text-black"
                        dangerouslySetInnerHTML={{ __html: sanitizeForm(option.label) }}
                      />
                      <input
                        id={name + option.id}
                        name={name}
                        type="radio"
                        value={option.value}
                        checked={isChecked}
                        disabled={option.disabled || disabled}
                        onChange={e => {
                          // Update React Hook Form
                          field.onChange(e.target.value);

                          handleDynamicClear(e, e.target.value);

                          // If this field has atLeastOneOf relationship, trigger validation on the related field
                          if (atLeastOneOf) {
                            const [petType, , indexStr] = name.split(".");
                            const index = Number(indexStr);
                            const relatedFieldName = petType + "." + atLeastOneOf + "." + index;
                            trigger(relatedFieldName);
                          }

                          if (onChange) {
                            onChange(e);
                          }
                          if (onClick) {
                            onClick(e);
                          }
                        }}
                        ref={ref}
                        className="d-none"
                      />
                    </label>
                  );
                })}
                {shouldShowError && errorMessage && (
                  <div className="text-red fs-14px">{errorMessage}</div>
                )}
              </>
            );
          }}
        />
      );
    }

  
    // Build validation rules for single radio button
    const validationRules = mapValidations(validations ? [validations] : []);
    
    // Add required validation if explicitly marked as required
    if (required && !validationRules.required) {
      validationRules.required = {
        value: true,
        message: "This field is required"
      };
    }
    
    // Add custom validation to handle empty string as invalid for required fields
    if (required || validationRules.required) {
      const existingValidate = validationRules.validate;
      validationRules.validate = (value: any) => {
        // For radio buttons, empty string, null, or undefined means not selected
        if (required && (!value || value === "")) {
          return validationRules.required?.message || "This field is required";
        }
        
        // Run any existing validation
        if (existingValidate && typeof existingValidate === 'function') {
          return existingValidate(value);
        }
        
        return true;
      };
    }

    return (
      <Controller
        name={name}
        control={control}
        defaultValue={initialValue}
        rules={validationRules}
        render={({ field }) => {
          const currentValue = field.value || "";
          const isChecked = value ? currentValue === value : checked;
          const iconName: IconName = isChecked ? "radioFilled" : "radioEmpty";
          
          return (
            <>
              <label
                htmlFor={id}
                className={`cursor-pointer d-inline-flex align-items-center gap-5px ${
                  disabled ? "opacity-50" : ""
                } ${className}`}
              >
                <span
                  role="radio"
                  aria-checked={isChecked ? "true" : "false"}
                  aria-disabled={disabled ? "true" : "false"}
                  title={label || "Radio option"}
                >
                  <Icon name={iconName} size={24} />
                </span>
                {label && (
                  <span className="fs-16px fw-normal text-black">{label}</span>
                )}
                <input
                  id={id}
                  name={name}
                  type="radio"
                  value={value}
                  checked={isChecked}
                  disabled={disabled}
                  onChange={(e) => {
                    field.onChange(value || e.target.value);
                    if (onChange) {
                      onChange(e);
                    }
                    if (onClick) {
                      onClick(e);
                    }
                  }}
                  ref={ref}
                  className="d-none"
                />
              </label>
              {errorMessage && <div className="text-red fs-14px">{errorMessage}</div>}
            </>
          );
        }}
      />
    );

    
  }
);

export default RadioInput;